import React, { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ReceiptIcon, ArrowLeft, ChevronRight, CalendarIcon, Search, Filter, ClipboardCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { eventService } from '@/services/eventService';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import SubscriptionRequiredModal from '@/components/payment/SubscriptionRequiredModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';

interface PendingBilling {
  studentId: string;
  studentName: string;
  eventCount: number;
  totalAmount: number;
  lastEventDate: string;
}

const InvoicesSchoolBilling = () => {
  const { t, locale } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState<string | undefined>(undefined);
  const [selectedMonth, setSelectedMonth] = useState<Date | undefined>(undefined);

  // Check if user has subscription or is in trial period
  const hasAccess = user?.is_subscribed || user?.is_trialing;

  useEffect(() => {
    // If user doesn't have access, show the subscription modal
    if (!hasAccess) {
      setShowSubscriptionModal(true);
    }
  }, [hasAccess]);

  const { data: pendingBillings = [], isLoading: pendingBillingsLoading } = useQuery({
    queryKey: ['pendingBillings', token],
    queryFn: () => eventService.getSchoolPendingBillings(token),
    // Only fetch data if user has access
    enabled: !!token && hasAccess,
  });

  const { data: pendingValidationEvents = [] } = useQuery({
    queryKey: ['pendingValidationEventsSchool', token],
    queryFn: () => eventService.getSchoolPendingValidationEvents(token),
    enabled: !!token && hasAccess,
  });

  const pendingValidationCount = React.useMemo(() => {
    const uniqueEventIds = new Set<string>();
    pendingValidationEvents.forEach((event: any) => {
      if (event.id) uniqueEventIds.add(String(event.id));
    });
    return uniqueEventIds.size;
  }, [pendingValidationEvents]);

  const hasPendingValidation = pendingValidationCount > 0;


  const { data: students = [], isLoading: studentsLoading } = useQuery({
    queryKey: ['teachers', token],
    queryFn: () => eventService.getTeachers(user, token),
  });

  const translateStr = (key: string, options?: Record<string, any>): string => {
    const translated = t(key, options);
    return Array.isArray(translated) ? translated.join(' ') : translated;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: user.currency_code || '$'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-US').format(new Date(dateString));
  };

  const handleStudentClick = (studentId: string) => {
    navigate(`/school/invoices/create/${studentId}`);
  };



  const filteredBillings = React.useMemo(() => {
    if (pendingBillingsLoading || !pendingBillings) return [];

    return pendingBillings.filter((billing: PendingBilling) => {
      let passes = true;
      if (searchTerm) {
        passes = billing.studentName.toLowerCase().includes(searchTerm.toLowerCase());
      }

      if (passes && selectedStudent) {
        passes = billing.studentName.includes(selectedStudent);
      }

      if (passes && selectedMonth) {
        const billingDate = new Date(billing.lastEventDate);
        passes = (
          billingDate.getMonth() === selectedMonth.getMonth() &&
          billingDate.getFullYear() === selectedMonth.getFullYear()
        );
      }

      return passes;
    });
  }, [pendingBillings, pendingBillingsLoading, searchTerm, selectedStudent, selectedMonth]);

  const resetFilters = () => {
    setSelectedStudent(undefined);
    setSelectedMonth(undefined);
    setSearchTerm("");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Subscription required modal */}
      <SubscriptionRequiredModal
        isOpen={showSubscriptionModal}
        onClose={() => {
          setShowSubscriptionModal(false);
          if (!hasAccess) {
            navigate('/invoices');
          }
        }}
      />

      <main className="flex-1 container py-8 mt-16">
        {hasAccess ? (
          <>
            <div>
              <Button
                variant="ghost"
                onClick={() => navigate('/invoices')}
                className="mb-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {translateStr('common.back')}
              </Button>
            </div>

            {hasPendingValidation && (
              <div className="mb-6 overflow-hidden rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 dark:border-amber-800 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 sm:p-5">
                  <div className="flex items-center gap-3">
                    <div className="hidden sm:flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                      <ClipboardCheck className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-amber-900 dark:text-amber-100">
                        {translateStr('invoices.pendingValidationBannerTitle')}
                      </h3>
                      <p className="text-sm text-amber-800 dark:text-amber-200/80">
                        {translateStr('invoices.pendingValidationBannerDescription', { count: pendingValidationCount })}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto sm:ml-auto">
                    <Button
                      size="sm"
                      onClick={() => navigate('/school/invoices/pending-validation')}
                      className="w-full sm:w-auto justify-center bg-amber-600 hover:bg-amber-700 text-white dark:bg-amber-600 dark:hover:bg-amber-500"
                    >
                      {translateStr('invoices.pendingValidation')}
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <Card>
              <CardHeader>
                <CardTitle className='pb-4'>{translateStr('invoices.pendingBillings')}</CardTitle>
                {/* <CardDescription>
                  {translateStr('invoices.pendingBillingsDescription')}
                </CardDescription> */}
                <div className="flex flex-col md:flex-row items-center gap-4 justify-between mt-4">
                  <div className="flex flex-1 gap-2">
                    <div className="relative md:w-56 w-full">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="search"
                        placeholder={translateStr('teachers.search')}
                        className="pl-8"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon">
                          <Filter className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[220px]">
                        <DropdownMenuLabel>{translateStr('invoices.filters')}</DropdownMenuLabel>
                        <DropdownMenuSeparator />

                        <div className="p-2">
                          <div className="mb-2">
                            <p className="text-xs mb-1 font-medium">{translateStr('invoices.teacher')}</p>
                            <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                              <SelectTrigger className="w-full h-8 text-xs">
                                <SelectValue placeholder={translateStr('invoices.filter.allTeachers')} />
                              </SelectTrigger>
                              <SelectContent>
                                {students.map((student: any) => (
                                  <SelectItem key={student.id} value={student.id}>
                                    {student.firstname} {student.lastname}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="mb-2">
                            <p className="text-xs mb-1 font-medium">{translateStr('invoices.filter.date')}</p>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  className="w-full h-8 text-xs justify-start font-normal"
                                  size="sm"
                                >
                                  {selectedMonth ? (
                                    format(selectedMonth, 'MMMM yyyy')
                                  ) : (
                                    <span>{translateStr('invoices.filter.selectDate')}</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <CalendarComponent
                                  mode="single"
                                  selected={selectedMonth}
                                  onSelect={setSelectedMonth}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>

                          <Button className="w-full mt-2" size="sm" variant="ghost" onClick={resetFilters}>
                            {translateStr('invoices.filter.resetFilters')}
                          </Button>
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {pendingBillingsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-4">
                        <Skeleton className="h-12 w-12 rounded-full" />
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-[250px]" />
                          <Skeleton className="h-4 w-[200px]" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : filteredBillings.length > 0 ? (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{translateStr('invoices.student')}</TableHead>
                          <TableHead>{translateStr('invoices.pendingEvents')}</TableHead>
                          <TableHead>{translateStr('invoices.lastEventDate')}</TableHead>
                          <TableHead className="text-right">{translateStr('invoices.estimatedAmount')}</TableHead>
                          <TableHead className="w-[100px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredBillings.map((billing) => (
                          <TableRow key={billing.studentId} className="cursor-pointer hover:bg-muted/50" onClick={() => handleStudentClick(billing.studentId)}>
                            <TableCell className="font-medium">{billing.studentName}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 hover:bg-blue-100">
                                {billing.eventCount} {translateStr('invoices.events')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                                {formatDate(billing.lastEventDate)}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(billing.totalAmount)}
                            </TableCell>
                            <TableCell>
                              <Button size="sm" className="w-full">
                                <span>{translateStr('invoices.createInvoice')}</span>
                                <ChevronRight className="h-4 w-4 ml-1" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {translateStr('invoices.noPendingBillings')}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}
      </main>

      <Footer />
    </div>
  );
};

export default InvoicesSchoolBilling;
